import { FC, forwardRef, InputHTMLAttributes, LegacyRef, ReactNode, useState } from 'react';
import { cn } from '@/utils/classNames.ts';
import { InputProps } from 'react-daisyui';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

type TextInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'color'> &
  Pick<InputProps, 'size' | 'color' | 'bordered'> & {
    label?: string;
    error?: string;
    wrapperClassName?: string;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
  } & { ref?: LegacyRef<HTMLInputElement> };

const TextInput: FC<TextInputProps> = forwardRef(
  (
    {
      label,
      error,
      wrapperClassName,
      startIcon,
      endIcon,
      size = 'md',
      color,
      bordered = true,
      onChange,
      type = 'text',
      placeholder,
      value,
      ...args
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);

    const isPlaceholderVisible = !value && !!placeholder && type === 'date';

    return (
      <div className={cn('form-control max-w-full w-full', wrapperClassName)}>
        {label ? (
          <label className="label">
            <span className="label-text">{label}</span>
          </label>
        ) : null}
        <div
          className={cn('input flex items-center justify-between max-w-full gap-2 relative', {
            'input-ghost': color === 'ghost',
            'input-primary': color === 'primary',
            'input-secondary': color === 'secondary',
            'input-accent': color === 'accent',
            'input-info': color === 'info',
            'input-success': color === 'success',
            'input-warning': color === 'warning',
            'input-error': error || color === 'error',
            'input-xs': size === 'xs',
            'input-sm': size === 'sm',
            'input-md': size === 'md' || size,
            'input-lg': size === 'lg',
            'input-bordered': bordered,
          })}
        >
          {startIcon ? startIcon : null}
          {isPlaceholderVisible ? (
            <span className="absolute text-text-secondary block md:hidden">{placeholder}</span>
          ) : null}
          <input
            className={cn('text-base grow overflow-auto', {
              'max-w-full': !startIcon && !endIcon,
              'max-w-[calc(100%-1rem)]': !!startIcon || !!endIcon,
              'max-w-[calc(100%-2rem)]': !!startIcon && !!endIcon,
            })}
            ref={ref}
            onFocus={(event) => {
              if (type !== 'date') return;

              const target = event.nativeEvent?.target;
              if (target) {
                // @ts-expect-error - This is a known issue with React's typings for event targets
                target.defaultValue = '';
              }
            }}
            onChange={(e) => {
              if (type === 'number') {
                if (args.max) {
                  if (Number(e.target.value) > Number(args.max)) {
                    e.target.value = `${args.max}`;
                  }
                }
                if (args.min) {
                  if (Number(e.target.value) < Number(args.min)) {
                    e.target.value = `${args.min}`;
                  }
                }
              }

              if (onChange) {
                onChange(e);
              }
            }}
            type={type === 'password' ? (showPassword ? 'text' : 'password') : type}
            value={value}
            placeholder={placeholder}
            {...args}
          />
          {type === 'password' ? (
            showPassword ? (
              <EyeSlashIcon className="w-5 h-5 cursor-pointer" onClick={() => setShowPassword((prev) => !prev)} />
            ) : (
              <EyeIcon className="w-5 h-5 cursor-pointer" onClick={() => setShowPassword((prev) => !prev)} />
            )
          ) : null}
          {endIcon ? endIcon : null}
        </div>
        {error ? (
          <label className="label">
            <span className="label-text-alt text-error">{error}</span>
          </label>
        ) : null}
      </div>
    );
  }
);

export default TextInput;
